import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import {
    DownloadStaticFileRequest,
    DownloadStaticFileService,
    InvoiceBatchUploadMethod,
    StaticFile
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Color, DownloadService, forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { DragulaService } from 'ng2-dragula';
import { IInvoiceUploadMethodOption, InvoiceFileUpload } from '../../invoices.models';
import { InvoiceBatchService } from '../invoice-batch.service';

@LifecycleAware()
@Component({
    selector: 'lsq-upload-invoices-zone',
    template: `
        <div id="lsq-upload-invoices-zone">
            <!-- Single-file (spreadsheet) upload -->
            <div
                *ngIf="
                    uploadMethod.method !== invoiceBatchUploadMethod.InvoiceAndBackupCombinedUpload
                "
                [ngClass]="invoiceRequirementsClassList"
            >
                <strong
                    i18n="
                        Heading for the list of data points required for each invoice in a
                        spreadsheet
                    "
                >
                    Invoices must include:
                </strong>
                <ul>
                    <li *ngFor="let requirement of invoiceRequirements">
                        <lsq-icon [type]="icons.CheckmarkSmall" [color]="color.Green"></lsq-icon>
                        {{ requirement }}
                    </li>
                </ul>
            </div>

            <!-- Multiple-file upload -->
            <div
                *ngIf="
                    uploadMethod.method === invoiceBatchUploadMethod.InvoiceAndBackupCombinedUpload
                "
                [ngClass]="invoiceRequirementsClassList"
            >
                <strong
                    i18n="Heading for the list of what must be included in each uploaded document"
                >
                    Each document must include:
                </strong>
                <ul>
                    <li>
                        <lsq-icon [type]="icons.CheckmarkSmall" [color]="color.Green"></lsq-icon>
                        <ng-container
                            *ngFor="let requirement of invoiceRequirements; let i = index"
                        >
                            {{ requirement
                            }}<ng-container *ngIf="i < invoiceRequirements.length - 1"
                                >,&nbsp;</ng-container
                            >
                        </ng-container>
                    </li>
                    <li [attr.aria-hidden]="true" class="invoices-must-include-and">AND</li>
                    <li>
                        <lsq-icon [type]="icons.CheckmarkSmall" [color]="color.Green"></lsq-icon>
                        <b
                            i18n="
                                The last item in the list of what must be included in each uploaded
                                document
                            "
                            >Supporting Documents</b
                        >
                        <lsq-icon-popover
                            text="Examples of supporting documents are: Proof of Delivery, a Purchase Order, Sales Order, etc."
                        ></lsq-icon-popover>
                    </li>
                </ul>
            </div>

            <ng-container *ngIf="!fileUploads?.length">
                <div
                    *ngIf="!isSmBelow"
                    class="fake-drop-zone d-flex flex-column justify-content-center"
                    [ngClass]="{ 'is-dragged-over': isDraggedOver }"
                >
                    <lsq-icon [type]="icons.UploadLarge"></lsq-icon>

                    <p>
                        <ng-container
                            *ngIf="
                                uploadMethod.method ===
                                    invoiceBatchUploadMethod.SpreadsheetAndBackupUpload;
                                else uploadImagesCopy
                            "
                            i18n="
                                Instructs the user to drag and drop a file from their computer onto
                                the page
                            "
                        >
                            Drag and drop your spreadsheet anywhere on the screen to upload.
                        </ng-container>
                        <ng-template
                            #uploadImagesCopy
                            i18n="
                                Instructs the user to drag and drop files from their computer onto
                                the page
                            "
                        >
                            Drag and drop invoices anywhere on the screen to upload.
                        </ng-template>
                    </p>

                    <div class="file-select-group">
                        <ng-container i18n>or&nbsp;</ng-container
                        ><a
                            href="javascript:void(0)"
                            (click)="fileSelect.emit()"
                            i18n="Button prompting the user to select file(s) to upload"
                            >{ (invoiceBatchService.isSpreadsheetUploadMethodSelected() ? 1 : 2),
                            plural, =1 {Select File} other {Select Files} }
                        </a>
                    </div>
                </div>

                <div *ngIf="isSmBelow" class="upload-btn-mobile full-width">
                    <lsq-button
                        [size]="'block'"
                        (buttonClick)="fileSelect.emit()"
                        i18n="Button prompting the user to select file(s) to upload (mobile view)"
                    >
                        Upload
                    </lsq-button>
                </div>

                <!-- TODO(dmayer): Un-comment once this feature has been implemented on the back end
                <ng-container *ngIf="uploadMethod.method === invoiceBatchUploadMethod.SpreadsheetAndBackupUpload">
                    <div class="info-alert-box">
                        <lsq-icon [type]="icons.Bell" [color]="color.GrayDark"></lsq-icon>
                        <p>
                            Is this your first time uploading a spreadsheet with invoices?<br/>If so, please download our template to make sure there are no delays in processing your invoices.
                        </p>
                        <a href="javascript:void(0)"
                            (click)="downloadSpreadsheetTemplate()">
                            <b>Download template</b>
                        </a>
                    </div>
                </ng-container>
                -->
            </ng-container>

            <!-- Draggable container -->
            <ng-container *ngIf="!isSpreadsheetUpload">
                <div
                    [hidden]="!fileUploads?.length"
                    class="upload-previews-container justify-content-start"
                    [dragula]="dragulaBagName"
                    [dragulaModel]="fileUploads"
                    (dragulaModelChange)="handleDragulaModelChange($event)"
                >
                    <ng-container
                        *ngTemplateOutlet="thumbnailPreview; context: { draggable: true }"
                    >
                    </ng-container>
                </div>
            </ng-container>

            <!-- Non-draggable container -->
            <ng-container *ngIf="isSpreadsheetUpload">
                <div
                    [hidden]="!fileUploads?.length"
                    class="upload-previews-container justify-content-center"
                >
                    <ng-container
                        *ngTemplateOutlet="thumbnailPreview; context: { draggable: false }"
                    >
                    </ng-container>
                </div>
            </ng-container>

            <ng-template #thumbnailPreview let-draggable="draggable">
                <div
                    *ngFor="let invoiceUpload of fileUploads; let i = index"
                    [ngClass]="{
                        'touch-action-manipulation': draggable
                    }"
                    class="upload-preview-container"
                    (touchmove)="blockTouchScrollingIfDraggable($event, draggable)"
                >
                    <lsq-upload-invoices-thumbnail-preview
                        [ordinal]="i + 1"
                        [invoiceUpload]="invoiceUpload"
                        [allowSupportingDocuments]="isSpreadsheetUpload"
                        (remove)="removeInvoiceUpload($event)"
                    >
                    </lsq-upload-invoices-thumbnail-preview>
                </div>
            </ng-template>
        </div>
    `,
    styleUrls: ['./upload-invoices-zone.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadInvoicesZoneComponent implements OnInit, OnDestroy {
    @Input() public uploadMethod: IInvoiceUploadMethodOption;
    @Input() public isDraggedOver = false;
    @Input() public fileUploads?: InvoiceFileUpload[];
    @Input() public isSpreadsheetUpload = false;
    @Output() public fileSelect = new EventEmitter<void>();
    @Output() public reorder = new EventEmitter<InvoiceFileUpload[]>();

    // Template bindings.
    public invoiceRequirements = [
        this._i18n({
            value: 'Invoice Number',
            meaning: `Signals that 'Invoice Number' must be included in each document or spreadsheet row`
        }),
        this._i18n({
            value: 'Invoice Date',
            meaning: `Signals that 'Invoice Date' must be included in each document or spreadsheet row`
        }),
        this._i18n({
            value: 'Invoice Terms',
            meaning: `Signals that 'Invoice Terms' must be included in each document or spreadsheet row`
        }),
        this._i18n({
            value: 'Customer Name',
            meaning: `Signals that 'Customer Name' must be included in each document or spreadsheet row`
        }),
        this._i18n({
            value: 'Customer Billing Address',
            meaning: `Signals that 'Customer Billing Address' must be included in each document or spreadsheet row`
        }),
        this._i18n({
            value: 'Invoice Total',
            meaning: `Signals that 'Invoice Total' must be included in each document or spreadsheet row`
        })
    ];
    public invoiceRequirementsClassList = [
        'invoices-must-include',
        'd-flex justify-content-center flex-wrap'
    ];
    public isSmBelow: boolean;
    public areThumbnailsDraggable = false;

    // Constants.
    public icons = Icons;
    public color = Color;
    public invoiceBatchUploadMethod = InvoiceBatchUploadMethod;
    public dragulaBagName = 'upload-invoices-previews';

    constructor(
        private _downloadService: DownloadService,
        private _downloadStaticFileService: DownloadStaticFileService,
        private _dragulaService: DragulaService,
        private _responsiveService: ResponsiveService,
        private _i18n: I18n,
        public invoiceBatchService: InvoiceBatchService
    ) {}

    public ngOnInit(): void {
        this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isSmBelow) => {
            this.isSmBelow = isSmBelow;
            this.areThumbnailsDraggable = !this.isSmBelow;

            if (!this._dragulaService.find(this.dragulaBagName)) {
                this._dragulaService.createGroup(this.dragulaBagName, {
                    direction: this.isSmBelow ? 'vertical' : 'horizontal'
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this._dragulaService.destroy(this.dragulaBagName);
    }

    public blockTouchScrollingIfDraggable(event: TouchEvent, shouldBlock: boolean): void {
        if (shouldBlock) {
            event.preventDefault();
        }
    }

    public downloadSpreadsheetTemplate(): void {
        const downloadStaticFileRequest = new DownloadStaticFileRequest({
            staticFile: StaticFile.InvoiceSummaryTemplate
        });
        this._downloadService.execute(
            this._downloadStaticFileService,
            downloadStaticFileRequest,
            FileType.Csv,
            'InvoiceSpreadsheetTemplate.csv'
        );
    }

    public removeInvoiceUpload(fileUpload: InvoiceFileUpload): void {
        if (fileUpload.linkedFileUploads.length > 0) {
            // In the event of a user removing the parent document, we want to remove all child documents linked to this parent.
            fileUpload.linkedFileUploads.forEach((file) => {
                this.invoiceBatchService.removeFileFromInvoiceBatch(file);
            });
        }

        this.invoiceBatchService.removeFileFromInvoiceBatch(fileUpload);
    }

    public handleDragulaModelChange(fileUploads: InvoiceFileUpload[]): void {
        this.reorder.emit(fileUploads);
    }
}
